import axios from 'axios';
import { config } from '../config';

const api = axios.create({
  baseURL: config.api.baseUrl
});

// Request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('📤 API REQUEST:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      params: config.params,
      data: config.data
    });
    return config;
  },
  (error) => {
    console.error('❌ REQUEST ERROR:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('📥 API RESPONSE:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('❌ RESPONSE ERROR:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    return Promise.reject(error);
  }
);

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  organization_name?: string;
  zip_code?: string;
  city?: string;
  state?: string;
  phone?: string;
  personal_email?: string;
  region?: string;
  security_clearance?: any;
  field_ready: boolean;
  is_active: boolean;
  lms_enabled: boolean;
  rms_enabled: boolean;
  rms_unlocked: boolean;
  created_at: string;
  updated_at: string;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserFilters {
  search?: string;
  organization?: string;
  role?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const userApi = {
  getUsers: async (page: number = 1, limit: number = 50, filters: UserFilters = {}): Promise<UsersResponse> => {
    const response = await api.get('/users', {
      params: { page, limit, ...filters }
    });
    return response.data;
  },

  getUser: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  createUser: async (user: Partial<User>): Promise<User> => {
    const response = await api.post('/users', user);
    return response.data;
  },

  updateUser: async (id: string, user: Partial<User>): Promise<User> => {
    const response = await api.put(`/users/${id}`, user);
    return response.data;
  },

  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  }
};

export const reportsApi = {
  getReport: async (reportType: string): Promise<any> => {
    const response = await api.get(`/reports/${reportType}`);
    return response.data;
  }
};

export interface UserChangeHistory {
  id: string;
  user_id: string;
  changed_by_user_id?: string;
  changed_by_email?: string;
  changed_by_first_name?: string;
  changed_by_last_name?: string;
  action: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  before_snapshot?: any;
  after_snapshot?: any;
  change_reason?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface UserHistoryResponse {
  history: UserChangeHistory[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserHistorySummary {
  totalChanges: number;
  firstChange: string | null;
  lastChange: string | null;
  changesByAction: Array<{
    action: string;
    count: number;
    last_occurrence: string;
  }>;
}

export const userHistoryApi = {
  getUserHistory: async (
    userId: string,
    page: number = 1,
    limit: number = 25,
    filters?: { action?: string; fieldName?: string }
  ) => {
    const response = await api.get(`/user-history/${userId}`, {
      params: { page, limit, ...filters }
    });
    return response;
  },

  getUserHistorySummary: async (userId: string) => {
    const response = await api.get(`/user-history/${userId}/summary`);
    return response;
  }
};

export const adminToolsApi = {
  getUserTasks: async (userId: string) => {
    const response = await api.get(`/admin-tools/user-tasks/${userId}`);
    return response.data;
  },

  unassignTask: async (taskId: string, userId: string, reason?: string) => {
    const response = await api.post('/admin-tools/unassign-task', {
      taskId,
      userId,
      reason
    });
    return response.data;
  },

  pauseUser: async (email: string) => {
    const response = await api.post('/admin-tools/pause-user', { email });
    return response.data;
  },

  unpauseUser: async (email: string) => {
    const response = await api.post('/admin-tools/unpause-user', { email });
    return response.data;
  }
};
